import React, { useState, useEffect } from 'react';
import { db, storage } from '../services/firebase';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, query, orderBy, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { FaPlus, FaEdit, FaTrash, FaReceipt, FaSearch, FaFileDownload, FaFileInvoice, FaChartLine, FaEye, FaCamera, FaUpload, FaChartBar, FaChartPie, FaArrowUp, FaArrowDown, FaCalendarAlt } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import './AdminAccountingPage.css';

const AdminAccountingPage = () => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [transactions, setTransactions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [branchSales, setBranchSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'income', 'expense', 'invoice'
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterDateRange, setFilterDateRange] = useState('all');
  const [receiptFile, setReceiptFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [branchSalesData, setBranchSalesData] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [productSales, setProductSales] = useState([]);
  const [productSalesData, setProductSalesData] = useState('');
  const [productSalesFile, setProductSalesFile] = useState(null);

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    category: '',
    description: '',
    source: '',
    vendor: '',
    note: '',
    type: 'expense', // 'income' or 'expense'
    receiptUrl: '',
    status: 'pending'
  });

  const [invoiceFormData, setInvoiceFormData] = useState({
    invoiceNumber: '',
    clientName: '',
    clientEmail: '',
    items: [{ description: '', quantity: 1, rate: 0, amount: 0 }],
    subtotal: 0,
    tax: 0,
    total: 0,
    dueDate: new Date().toISOString().split('T')[0],
    status: 'unpaid',
    notes: ''
  });

  const expenseCategories = [
    'office_supplies', 'utilities', 'rent', 'marketing', 'travel', 
    'meals', 'software', 'equipment', 'insurance', 'professional_services', 
    'maintenance', 'fuel', 'other'
  ];

  const incomeCategories = [
    'sales', 'services', 'consulting', 'royalties', 'investments', 
    'grants', 'refunds', 'other'
  ];

  useEffect(() => {
    if (currentUser) {
      fetchTransactions();
      fetchInvoices();
      fetchBranchSales();
      fetchProductSales();
    }
  }, [currentUser]);

  const fetchTransactions = async () => {
    try {
      if (!currentUser || !currentUser.isAdmin) {
        console.error('User not authenticated or not admin');
        setLoading(false);
        return;
      }
      
      const q = query(collection(db, 'transactions'), orderBy('date', 'desc'));
      const querySnapshot = await getDocs(q);
      const transactionsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTransactions(transactionsList);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    try {
      if (!currentUser || !currentUser.isAdmin) {
        console.error('User not authenticated or not admin');
        return;
      }
      
      const q = query(collection(db, 'invoices'), orderBy('dueDate', 'desc'));
      const querySnapshot = await getDocs(q);
      const invoicesList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setInvoices(invoicesList);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  };

  const fetchBranchSales = async () => {
    try {
      if (!currentUser || !currentUser.isAdmin) {
        console.error('User not authenticated or not admin');
        return;
      }
      
      const q = query(collection(db, 'branchSales'), orderBy('month', 'desc'));
      const querySnapshot = await getDocs(q);
      const branchSalesList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBranchSales(branchSalesList);
    } catch (error) {
      console.error('Error fetching branch sales:', error);
    }
  };

  const fetchProductSales = async () => {
    try {
      if (!currentUser || !currentUser.isAdmin) {
        console.error('User not authenticated or not admin');
        return;
      }
      
      const q = query(collection(db, 'productSales'), orderBy('month', 'desc'));
      const querySnapshot = await getDocs(q);
      const productSalesList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProductSales(productSalesList);
    } catch (error) {
      console.error('Error fetching product sales:', error);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return null;
    
    setUploading(true);
    try {
      const storageRef = ref(storage, `receipts/${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmitTransaction = async (e) => {
    e.preventDefault();
    
    try {
      let receiptUrl = formData.receiptUrl;
      
      if (receiptFile) {
        receiptUrl = await handleFileUpload(receiptFile);
      }

      const transactionData = {
        ...formData,
        amount: parseFloat(formData.amount),
        receiptUrl,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      if (editingTransaction) {
        await updateDoc(doc(db, 'transactions', editingTransaction.id), {
          ...transactionData,
          updatedAt: new Date()
        });
      } else {
        await addDoc(collection(db, 'transactions'), transactionData);
      }

      resetForm();
      setShowModal(false);
      fetchTransactions();
    } catch (error) {
      console.error('Error saving transaction:', error);
    }
  };

  const handleSubmitInvoice = async (e) => {
    e.preventDefault();
    
    try {
      const invoiceData = {
        ...invoiceFormData,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      if (editingTransaction) {
        await updateDoc(doc(db, 'invoices', editingTransaction.id), {
          ...invoiceData,
          updatedAt: new Date()
        });
      } else {
        await addDoc(collection(db, 'invoices'), invoiceData);
      }

      resetInvoiceForm();
      setShowModal(false);
      fetchInvoices();
    } catch (error) {
      console.error('Error saving invoice:', error);
    }
  };

  const handleDeleteTransaction = async (id) => {
    if (window.confirm('Are you sure you want to delete this transaction?')) {
      try {
        await deleteDoc(doc(db, 'transactions', id));
        
        // Refresh all data to update dashboard
        fetchTransactions();
        
      } catch (error) {
        console.error('Error deleting transaction:', error);
      }
    }
  };

  const handleDeleteInvoice = async (id) => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      try {
        await deleteDoc(doc(db, 'invoices', id));
        fetchInvoices();
      } catch (error) {
        console.error('Error deleting invoice:', error);
      }
    }
  };

  const parseBranchSalesData = (dataString) => {
    try {
      const lines = dataString.trim().split('\n');
      console.log('Number of lines:', lines.length);
      
      if (lines.length < 2) {
        throw new Error('Data must have at least a header row and one data row');
      }

      // Find the actual data rows (skip header rows)
      const dataRows = [];
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue; // Skip empty lines
        
        const values = line.split('\t');
        console.log(`Line ${i} values:`, values);
        console.log(`Line ${i} number of values:`, values.length);
        
        // Skip header rows (they contain text like "Branch", "Gross Sales", etc.)
        const firstValue = values[0]?.trim();
        if (firstValue === 'Branch' || firstValue.includes('Branch Reference') || firstValue.includes('Gross Sales')) {
          console.log(`Skipping header row ${i}`);
          continue;
        }
        
        // Check if this looks like a data row (first column should be a branch name)
        if (firstValue && firstValue !== '' && values.length >= 20) {
          dataRows.push(values);
        }
      }

      console.log('Found data rows:', dataRows.length);
      
      if (dataRows.length === 0) {
        throw new Error('No data rows found. Please check your data format.');
      }

      const salesData = [];

      dataRows.forEach((values, index) => {
        console.log(`Processing data row ${index}:`, values);
        
        // Handle the case where data might be split across or have extra columns
        const branchData = {
          branch: values[0] || '',
          branchReference: values[1] || '',
          grossSales: parseFloat(values[2]?.replace(/,/g, '')) || 0,
          grossSalesPercentage: values[3] || '',
          netSalesWithTax: parseFloat(values[4]?.replace(/,/g, '')) || 0,
          taxes: parseFloat(values[5]?.replace(/,/g, '')) || 0,
          discountAmount: parseFloat(values[6]?.replace(/,/g, '')) || 0,
          grossSalesWithoutTax: parseFloat(values[7]?.replace(/,/g, '')) || 0,
          netSales: parseFloat(values[8]?.replace(/,/g, '')) || 0,
          netSalesPercentage: values[9] || '',
          orderCount: parseInt(values[10]?.replace(/,/g, '')) || 0,
          averageOrder: parseFloat(values[11]?.replace(/,/g, '')) || 0,
          guestCount: parseInt(values[12]?.replace(/,/g, '')) || 0,
          averagePerGuest: parseFloat(values[13]?.replace(/,/g, '')) || 0,
          customersCount: parseInt(values[14]?.replace(/,/g, '')) || 0,
          netQuantity: parseInt(values[15]?.replace(/,/g, '')) || 0,
          cost: parseFloat(values[16]?.replace(/,/g, '')) || 0,
          returnAmount: parseFloat(values[17]?.replace(/,/g, '')) || 0,
          returnQuantity: parseInt(values[18]?.replace(/,/g, '')) || 0,
          voidAmount: parseFloat(values[19]?.replace(/,/g, '')) || 0,
          voidQuantity: parseInt(values[20]?.replace(/,/g, '')) || 0,
          profit: parseFloat(values[21]?.replace(/,/g, '')) || 0,
          charges: parseFloat(values[22]?.replace(/,/g, '')) || 0,
          roundingAmount: parseFloat(values[23]?.replace(/,/g, '')) || 0,
          tips: parseFloat(values[24]?.replace(/,/g, '')) || 0
        };
        
        console.log('Parsed branch data:', branchData);
        salesData.push(branchData);
      });

      console.log('Total parsed records:', salesData.length);
      return salesData;
    } catch (error) {
      console.error('Error parsing branch sales data:', error);
      throw error;
    }
  };

  const handleBranchSalesImport = async () => {
    if (!branchSalesData.trim()) {
      alert('Please paste your branch sales data');
      return;
    }

    try {
      console.log('Starting import process...');
      console.log('Raw data:', branchSalesData);
      
      const parsedData = parseBranchSalesData(branchSalesData);
      
      if (parsedData.length === 0) {
        throw new Error('No valid data rows found. Please check your data format.');
      }

      console.log('Parsed data successfully:', parsedData);
      
      // Save to Firebase
      const salesRecord = {
        month: selectedMonth,
        data: parsedData,
        totalGrossSales: parsedData.reduce((sum, branch) => sum + (branch.grossSales || 0), 0),
        totalNetSales: parsedData.reduce((sum, branch) => sum + (branch.netSales || 0), 0),
        totalProfit: parsedData.reduce((sum, branch) => sum + (branch.profit || 0), 0),
        totalOrders: parsedData.reduce((sum, branch) => sum + (branch.orderCount || 0), 0),
        createdAt: new Date(),
        updatedAt: new Date()
      };

      console.log('Saving to Firebase:', salesRecord);
      
      try {
        await addDoc(collection(db, 'branchSales'), salesRecord);
        console.log('Successfully saved to Firebase');
      } catch (firebaseError) {
        console.error('Firebase save failed, using local storage backup:', firebaseError);
        
        // Save to local storage as backup
        const localData = JSON.parse(localStorage.getItem('branchSalesBackup') || '[]');
        localData.push({...salesRecord, id: Date.now().toString()});
        localStorage.setItem('branchSalesBackup', JSON.stringify(localData));
        
        alert('Data saved to local storage as backup due to connection issues. Please try again later or contact support.');
      }
      
      // Auto-generate transactions from branch data
      console.log('Generating transactions...');
      await generateTransactionsFromBranchData(parsedData, selectedMonth);
      
      setBranchSalesData('');
      fetchBranchSales();
      fetchTransactions();
      
      alert(`Branch sales data imported successfully! ${parsedData.length} branches processed.`);
    } catch (error) {
      console.error('Error importing branch sales data:', error);
      alert(`Error importing data: ${error.message}\n\nPlease check the browser console for more details.`);
    }
  };

  const generateTransactionsFromBranchData = async (branchData, month) => {
    const transactions = [];
    
    branchData.forEach(branch => {
      // Generate income transaction for net sales
      if (branch.netSales > 0) {
        transactions.push({
          date: `${month}-01`,
          amount: branch.netSales,
          category: 'sales',
          description: `${branch.branch} - Net Sales`,
          source: branch.branch,
          type: 'income',
          status: 'completed',
          branch: branch.branch,
          branchReference: branch.branchReference,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }

      // Generate expense transaction for costs if available
      if (branch.cost > 0) {
        transactions.push({
          date: `${month}-01`,
          amount: branch.cost,
          category: 'other',
          description: `${branch.branch} - Operating Costs`,
          vendor: branch.branch,
          type: 'expense',
          status: 'completed',
          branch: branch.branch,
          branchReference: branch.branchReference,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }
    });

    // Save all transactions
    for (const transaction of transactions) {
      await addDoc(collection(db, 'transactions'), transaction);
    }
  };

  const handleDeleteBranchSales = async (saleId) => {
    if (!window.confirm('Are you sure you want to delete this branch sales record? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'branchSales', saleId));
      
      // Refresh all data to update dashboard
      fetchBranchSales();
      fetchTransactions();
      
      alert('Branch sales record deleted successfully!');
    } catch (error) {
      console.error('Error deleting branch sales:', error);
      alert('Error deleting branch sales record.');
    }
  };

  const handleResetAllTransactions = async () => {
    const confirmMessage = `⚠️ DANGER: This will permanently delete ALL transactions in the system.\n\nThis action cannot be undone. Are you absolutely sure?\n\nType "DELETE ALL" to confirm:`;
    
    const userInput = prompt(confirmMessage);
    if (userInput !== 'DELETE ALL') {
      alert('Reset cancelled.');
      return;
    }

    try {
      setLoading(true);
      const q = query(collection(db, 'transactions'));
      const querySnapshot = await getDocs(q);
      
      const deletePromises = querySnapshot.docs.map(docSnapshot => 
        deleteDoc(doc(db, 'transactions', docSnapshot.id))
      );
      
      await Promise.all(deletePromises);
      
      setTransactions([]);
      
      // Force refresh of all data to update dashboard
      fetchTransactions();
      fetchInvoices();
      fetchBranchSales();
      
      alert(`Successfully deleted ${querySnapshot.docs.length} transactions.`);
    } catch (error) {
      console.error('Error resetting transactions:', error);
      alert('Error resetting transactions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetAllBranchSales = async () => {
    const confirmMessage = `⚠️ DANGER: This will permanently delete ALL branch sales records.\n\nThis action cannot be undone. Are you absolutely sure?\n\nType "DELETE ALL" to confirm:`;
    
    const userInput = prompt(confirmMessage);
    if (userInput !== 'DELETE ALL') {
      alert('Reset cancelled.');
      return;
    }

    try {
      setLoading(true);
      const q = query(collection(db, 'branchSales'));
      const querySnapshot = await getDocs(q);
      
      const deletePromises = querySnapshot.docs.map(docSnapshot => 
        deleteDoc(doc(db, 'branchSales', docSnapshot.id))
      );
      
      await Promise.all(deletePromises);
      
      setBranchSales([]);
      
      // Force refresh of all data to update dashboard
      fetchTransactions();
      fetchInvoices();
      fetchBranchSales();
      
      alert(`Successfully deleted ${querySnapshot.docs.length} branch sales records.`);
    } catch (error) {
      console.error('Error resetting branch sales:', error);
      alert('Error resetting branch sales. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetAllInvoices = async () => {
    const confirmMessage = `⚠️ DANGER: This will permanently delete ALL invoices.\n\nThis action cannot be undone. Are you absolutely sure?\n\nType "DELETE ALL" to confirm:`;
    
    const userInput = prompt(confirmMessage);
    if (userInput !== 'DELETE ALL') {
      alert('Reset cancelled.');
      return;
    }

    try {
      setLoading(true);
      const q = query(collection(db, 'invoices'));
      const querySnapshot = await getDocs(q);
      
      const deletePromises = querySnapshot.docs.map(docSnapshot => 
        deleteDoc(doc(db, 'invoices', docSnapshot.id))
      );
      
      await Promise.all(deletePromises);
      
      setInvoices([]);
      
      // Force refresh of all data to update dashboard
      fetchTransactions();
      fetchInvoices();
      fetchBranchSales();
      
      alert(`Successfully deleted ${querySnapshot.docs.length} invoices.`);
    } catch (error) {
      console.error('Error resetting invoices:', error);
      alert('Error resetting invoices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetAllAccountingData = async () => {
    const confirmMessage = `🚨 EXTREME DANGER: This will permanently delete ALL accounting data:\n\n• All Transactions\n• All Branch Sales Records\n• All Invoices\n\nThis action cannot be undone and will wipe your entire accounting system.\n\nType "RESET EVERYTHING" to confirm:`;
    
    const userInput = prompt(confirmMessage);
    if (userInput !== 'RESET EVERYTHING') {
      alert('Reset cancelled.');
      return;
    }

    try {
      setLoading(true);
      
      // Delete all transactions
      const transactionsQuery = query(collection(db, 'transactions'));
      const transactionsSnapshot = await getDocs(transactionsQuery);
      const deleteTransactionsPromises = transactionsSnapshot.docs.map(docSnapshot => 
        deleteDoc(doc(db, 'transactions', docSnapshot.id))
      );
      
      // Delete all branch sales
      const branchSalesQuery = query(collection(db, 'branchSales'));
      const branchSalesSnapshot = await getDocs(branchSalesQuery);
      const deleteBranchSalesPromises = branchSalesSnapshot.docs.map(docSnapshot => 
        deleteDoc(doc(db, 'branchSales', docSnapshot.id))
      );
      
      // Delete all invoices
      const invoicesQuery = query(collection(db, 'invoices'));
      const invoicesSnapshot = await getDocs(invoicesQuery);
      const deleteInvoicesPromises = invoicesSnapshot.docs.map(docSnapshot => 
        deleteDoc(doc(db, 'invoices', docSnapshot.id))
      );
      
      // Execute all deletions
      await Promise.all([
        ...deleteTransactionsPromises,
        ...deleteBranchSalesPromises,
        ...deleteInvoicesPromises
      ]);
      
      // Reset state
      setTransactions([]);
      setBranchSales([]);
      setInvoices([]);
      
      // Force refresh of all data to update dashboard
      fetchTransactions();
      fetchInvoices();
      fetchBranchSales();
      
      const totalDeleted = transactionsSnapshot.docs.length + branchSalesSnapshot.docs.length + invoicesSnapshot.docs.length;
      alert(`Successfully reset entire accounting system. Deleted ${totalDeleted} records total.`);
    } catch (error) {
      console.error('Error resetting all accounting data:', error);
      alert('Error resetting accounting data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      amount: '',
      category: '',
      description: '',
      source: '',
      vendor: '',
      note: '',
      type: 'expense',
      receiptUrl: '',
      status: 'pending'
    });
    setReceiptFile(null);
    setEditingTransaction(null);
  };

  const resetInvoiceForm = () => {
    setInvoiceFormData({
      invoiceNumber: '',
      clientName: '',
      clientEmail: '',
      items: [{ description: '', quantity: 1, rate: 0, amount: 0 }],
      subtotal: 0,
      tax: 0,
      total: 0,
      dueDate: new Date().toISOString().split('T')[0],
      status: 'unpaid',
      notes: ''
    });
    setEditingTransaction(null);
  };

  const calculateDashboardStats = () => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    console.log('Calculating dashboard stats with transactions:', transactions.length);
    
    const monthlyTransactions = transactions.filter(t => {
      if (!t.date) return false;
      const transactionDate = new Date(t.date);
      return transactionDate.getMonth() === currentMonth && transactionDate.getFullYear() === currentYear;
    });

    console.log('Monthly transactions:', monthlyTransactions.length);

    const monthlyIncome = monthlyTransactions
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    const monthlyExpenses = monthlyTransactions
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    const netCashFlow = monthlyIncome - monthlyExpenses;

    console.log('Monthly income:', monthlyIncome);
    console.log('Monthly expenses:', monthlyExpenses);
    console.log('Net cash flow:', netCashFlow);

    const overdueInvoices = invoices.filter(inv => {
      if (!inv.dueDate) return false;
      const dueDate = new Date(inv.dueDate);
      return inv.status === 'unpaid' && dueDate < new Date();
    });

    const upcomingBills = transactions.filter(t => {
      if (!t.date) return false;
      const transactionDate = new Date(t.date);
      const nextWeek = new Date();
      nextWeek.setDate(nextWeek.getDate() + 7);
      return t.type === 'expense' && t.status === 'pending' && transactionDate <= nextWeek;
    });

    return {
      monthlyIncome,
      monthlyExpenses,
      netCashFlow,
      overdueInvoices,
      upcomingBills
    };
  };

  // Product Sales Functions
  const parseProductSalesData = (dataString) => {
    try {
      console.log('Raw data string length:', dataString.length);
      console.log('First 200 chars:', dataString.substring(0, 200));
      
      const lines = dataString.trim().split('\n');
      console.log('Number of lines:', lines.length);
      
      if (lines.length < 1) {
        throw new Error('No data provided');
      }

      const dataRows = [];
      let headerFound = false;
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        
        let values = [];
        
        // Try different parsing methods
        if (line.includes('\t')) {
          // Tab-separated
          values = line.split('\t');
        } else if (line.includes(',')) {
          // CSV with proper comma handling for quoted fields
          values = line.match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g) || line.split(',');
          values = values.map(v => v.replace(/^["']|["']$/g, '').trim());
        } else {
          // Space-separated
          values = line.split(/\s+/);
        }
        
        // Pad array to ensure 17 elements
        while (values.length < 17) {
          values.push('');
        }
        
        console.log(`Line ${i} (${values.length} values):`, values.slice(0, 5), '...');
        
        const firstValue = values[0]?.trim();
        
        // Header detection
        if (firstValue === 'Product' || 
            firstValue.toLowerCase().includes('product') || 
            line.toLowerCase().includes('product sku') || 
            line.toLowerCase().includes('gross sales')) {
          console.log(`Skipping header row ${i}: ${firstValue}`);
          headerFound = true;
          continue;
        }
        
        // Check if this looks like a valid data row (has product name and at least some numeric data)
        if (firstValue && firstValue !== '' && !isNaN(parseFloat(values[2]?.toString().replace(/[^\d.-]/g, '') || '0'))) {
          try {
            const productData = {
              product: values[0]?.trim() || '',
              productSku: values[1]?.trim() || '',
              grossSales: parseFloat(values[2]?.toString().replace(/[^\d.-]/g, '') || '0') || 0,
              grossSalesPercent: values[3]?.trim() || '',
              netSalesWithTax: parseFloat(values[4]?.toString().replace(/[^\d.-]/g, '') || '0') || 0,
              taxes: parseFloat(values[5]?.toString().replace(/[^\d.-]/g, '') || '0') || 0,
              discountAmount: parseFloat(values[6]?.toString().replace(/[^\d.-]/g, '') || '0') || 0,
              grossSalesWithoutTax: parseFloat(values[7]?.toString().replace(/[^\d.-]/g, '') || '0') || 0,
              netSales: parseFloat(values[8]?.toString().replace(/[^\d.-]/g, '') || '0') || 0,
              netSalesPercent: values[9]?.trim() || '',
              netQuantity: parseInt(values[10]?.toString().replace(/[^\d]/g, '') || '0') || 0,
              cost: parseFloat(values[11]?.toString().replace(/[^\d.-]/g, '') || '0') || 0,
              returnAmount: parseFloat(values[12]?.toString().replace(/[^\d.-]/g, '') || '0') || 0,
              returnQuantity: parseInt(values[13]?.toString().replace(/[^\d]/g, '') || '0') || 0,
              voidAmount: parseFloat(values[14]?.toString().replace(/[^\d.-]/g, '') || '0') || 0,
              voidQuantity: parseInt(values[15]?.toString().replace(/[^\d]/g, '') || '0') || 0,
              profit: parseFloat(values[16]?.toString().replace(/[^\d.-]/g, '') || '0') || 0
            };
            
            console.log(`Parsed product ${i}:`, productData.product, productData.grossSales);
            dataRows.push(productData);
          } catch (parseError) {
            console.warn(`Error parsing line ${i}:`, parseError, values);
          }
        } else {
          console.log(`Skipping line ${i}: "${firstValue}" - no valid product name or numeric data`);
        }
      }
      
      console.log(`Successfully parsed ${dataRows.length} products`);
      console.log('Sample parsed data:', dataRows.slice(0, 3));
      
      if (dataRows.length === 0) {
        throw new Error(`No valid product data found. 

Expected format with 17 columns:
Product | Product SKU | Gross Sales | Gross Sales % | Net Sales With Tax | Taxes | Discount Amount | Gross Sales Without Tax | Net Sales | Net Sales % | Net Quantity | Cost | Return Amount | Return Quantity | Void Amount | Void Quantity | Profit

Check the browser console for detailed parsing information.`);
      }
      
      return dataRows;
    } catch (error) {
      console.error('Error parsing product sales data:', error);
      throw new Error(`Failed to parse data: ${error.message}`);
    }
  };

  const handleProductSalesFileUpload = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          resolve(text);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Error reading file'));
      reader.readAsText(file);
    });
  };

  const handleImportProductSales = async () => {
    let dataToProcess = productSalesData;

    // If a file is selected, read it first
    if (productSalesFile) {
      try {
        setLoading(true);
        console.log('Reading CSV file:', productSalesFile.name);
        dataToProcess = await handleProductSalesFileUpload(productSalesFile);
        console.log('File content length:', dataToProcess.length);
      } catch (error) {
        console.error('Error reading file:', error);
        window.alert('Error reading the uploaded file. Please try again.');
        setLoading(false);
        return;
      }
    }

    if (!dataToProcess || !dataToProcess.trim()) {
      window.alert('Please either paste data or upload a CSV file.');
      setLoading(false);
      return;
    }

    try {
      if (!loading) setLoading(true);
      const parsedData = parseProductSalesData(dataToProcess);
      
      if (parsedData.length === 0) {
        throw new Error('No valid product data found. Please check your data format.');
      }
      
      const salesRecord = {
        month: selectedMonth,
        data: parsedData,
        totalProducts: parsedData.length,
        totalGrossSales: parsedData.reduce((sum, product) => sum + (product.grossSales || 0), 0),
        totalNetSales: parsedData.reduce((sum, product) => sum + (product.netSales || 0), 0),
        totalProfit: parsedData.reduce((sum, product) => sum + (product.profit || 0), 0),
        totalQuantity: parsedData.reduce((sum, product) => sum + (product.netQuantity || 0), 0),
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      await addDoc(collection(db, 'productSales'), salesRecord);
      await fetchProductSales();
      setProductSalesData('');
      window.alert('Product sales data imported successfully!');
    } catch (error) {
      console.error('Error importing product sales:', error);
      window.alert('Error importing data. Please check the format.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProductSales = async (salesId) => {
    if (!window.confirm('Are you sure you want to delete this product sales record?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'productSales', salesId));
      await fetchProductSales();
      window.alert('Product sales record deleted successfully!');
    } catch (error) {
      console.error('Error deleting product sales:', error);
      window.alert('Error deleting product sales record.');
    }
  };

  const handleResetAllProductSales = async () => {
    const confirmMessage = `⚠️ DANGER: This will permanently delete ALL product sales records.\n\nThis action cannot be undone. Are you absolutely sure?\n\nType "DELETE ALL" to confirm:`;
    
    const userInput = window.prompt(confirmMessage);
    if (userInput !== 'DELETE ALL') {
      window.alert('Reset cancelled.');
      return;
    }

    try {
      setLoading(true);
      const q = query(collection(db, 'productSales'));
      const querySnapshot = await getDocs(q);
      
      const deletePromises = querySnapshot.docs.map(docSnapshot => 
        deleteDoc(doc(db, 'productSales', docSnapshot.id))
      );
      
      await Promise.all(deletePromises);
      await fetchProductSales();
      window.alert('All product sales records have been deleted.');
    } catch (error) {
      console.error('Error resetting product sales:', error);
      window.alert('Error resetting product sales.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => `QAR ${(amount || 0).toFixed(2)}`;

  // Analytics Functions
  const getMonthlyTrends = () => {
    const last12Months = [];
    const currentDate = new Date();
    
    for (let i = 11; i >= 0; i--) {
      const month = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const monthKey = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}`;
      
      const monthlyTransactions = transactions.filter(t => {
        if (!t.date) return false;
        const transactionDate = new Date(t.date);
        return transactionDate.getFullYear() === month.getFullYear() && 
               transactionDate.getMonth() === month.getMonth();
      });

      const income = monthlyTransactions
        .filter(t => t.type === 'income')
        .reduce((sum, t) => sum + (t.amount || 0), 0);
      
      const expenses = monthlyTransactions
        .filter(t => t.type === 'expense')
        .reduce((sum, t) => sum + (t.amount || 0), 0);

      last12Months.push({
        month: monthKey,
        monthName: month.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        income,
        expenses,
        profit: income - expenses
      });
    }
    
    return last12Months;
  };

  const getCategoryAnalysis = () => {
    const incomeByCategory = {};
    const expensesByCategory = {};
    
    transactions.forEach(t => {
      if (t.type === 'income') {
        incomeByCategory[t.category] = (incomeByCategory[t.category] || 0) + (t.amount || 0);
      } else if (t.type === 'expense') {
        expensesByCategory[t.category] = (expensesByCategory[t.category] || 0) + (t.amount || 0);
      }
    });

    return {
      income: Object.entries(incomeByCategory).map(([category, amount]) => ({
        category: category.replace('_', ' ').toUpperCase(),
        amount,
        percentage: 0 // Will calculate in component
      })),
      expenses: Object.entries(expensesByCategory).map(([category, amount]) => ({
        category: category.replace('_', ' ').toUpperCase(),
        amount,
        percentage: 0 // Will calculate in component
      }))
    };
  };

  const getBranchAnalysis = () => {
    const branchPerformance = {};
    
    branchSales.forEach(record => {
      if (record.data) {
        record.data.forEach(branch => {
          if (!branchPerformance[branch.branch]) {
            branchPerformance[branch.branch] = {
              totalSales: 0,
              totalOrders: 0,
              totalProfit: 0,
              months: 0
            };
          }
          
          branchPerformance[branch.branch].totalSales += branch.netSales || 0;
          branchPerformance[branch.branch].totalOrders += branch.orderCount || 0;
          branchPerformance[branch.branch].totalProfit += branch.profit || 0;
          branchPerformance[branch.branch].months += 1;
        });
      }
    });

    return Object.entries(branchPerformance).map(([branch, data]) => ({
      branch,
      ...data,
      avgMonthlySales: data.totalSales / (data.months || 1),
      avgMonthlyOrders: data.totalOrders / (data.months || 1),
      avgOrderValue: data.totalSales / (data.totalOrders || 1)
    }));
  };

  const getTopPerformers = () => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    const thisMonthTransactions = transactions.filter(t => {
      if (!t.date) return false;
      const date = new Date(t.date);
      return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
    });

    const topIncomes = thisMonthTransactions
      .filter(t => t.type === 'income')
      .sort((a, b) => (b.amount || 0) - (a.amount || 0))
      .slice(0, 5);

    const topExpenses = thisMonthTransactions
      .filter(t => t.type === 'expense')
      .sort((a, b) => (b.amount || 0) - (a.amount || 0))
      .slice(0, 5);

    return { topIncomes, topExpenses };
  };

  const getFinancialKPIs = () => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const lastMonth = new Date(currentYear, currentMonth - 1, 1);
    
    const currentMonthTransactions = transactions.filter(t => {
      if (!t.date) return false;
      const date = new Date(t.date);
      return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
    });

    const lastMonthTransactions = transactions.filter(t => {
      if (!t.date) return false;
      const date = new Date(t.date);
      return date.getMonth() === lastMonth.getMonth() && date.getFullYear() === lastMonth.getFullYear();
    });

    const currentIncome = currentMonthTransactions
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    const currentExpenses = currentMonthTransactions
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    const lastIncome = lastMonthTransactions
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    const lastExpenses = lastMonthTransactions
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    const incomeGrowth = lastIncome > 0 ? ((currentIncome - lastIncome) / lastIncome) * 100 : 0;
    const expenseGrowth = lastExpenses > 0 ? ((currentExpenses - lastExpenses) / lastExpenses) * 100 : 0;
    const profitMargin = currentIncome > 0 ? ((currentIncome - currentExpenses) / currentIncome) * 100 : 0;

    return {
      incomeGrowth,
      expenseGrowth,
      profitMargin,
      currentIncome,
      currentExpenses,
      lastIncome,
      lastExpenses
    };
  };

  const filteredTransactions = transactions.filter(transaction => {
    const matchesSearch = transaction.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         transaction.vendor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         transaction.source?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = filterCategory === 'all' || transaction.category === filterCategory;
    
    let matchesDateRange = true;
    if (filterDateRange !== 'all') {
      const transactionDate = new Date(transaction.date);
      const now = new Date();
      
      switch (filterDateRange) {
        case 'this_month':
          matchesDateRange = transactionDate.getMonth() === now.getMonth() && 
                           transactionDate.getFullYear() === now.getFullYear();
          break;
        case 'last_month':
          const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          matchesDateRange = transactionDate.getMonth() === lastMonth.getMonth() && 
                           transactionDate.getFullYear() === lastMonth.getFullYear();
          break;
        case 'this_year':
          matchesDateRange = transactionDate.getFullYear() === now.getFullYear();
          break;
      }
    }

    return matchesSearch && matchesCategory && matchesDateRange;
  });

  const openModal = (type, transaction = null) => {
    setModalType(type);
    setEditingTransaction(transaction);
    
    if (transaction) {
      if (type === 'invoice') {
        setInvoiceFormData(transaction);
      } else {
        setFormData({...transaction, type});
      }
    } else {
      // Reset form for new entries
      if (type === 'invoice') {
        resetInvoiceForm();
      } else {
        resetForm();
        setFormData(prev => ({...prev, type}));
      }
    }
    
    setShowModal(true);
  };

  const stats = calculateDashboardStats();

  if (!currentUser) {
    return <div className="loading">Loading user data...</div>;
  }

  if (!currentUser.isAdmin) {
    return <div className="admin-error">Access denied. Admin privileges required.</div>;
  }

  if (loading) {
    return <div className="loading">Loading accounting data...</div>;
  }

  return (
    <div className="accounting-page">
      <div className="accounting-header">
        <h1>Accounting System</h1>
        <div className="accounting-nav">
          <button 
            className={`nav-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            <FaChartLine /> Dashboard
          </button>
          <button 
            className={`nav-btn ${activeTab === 'transactions' ? 'active' : ''}`}
            onClick={() => setActiveTab('transactions')}
          >
            <FaReceipt /> Transactions
          </button>
          <button 
            className={`nav-btn ${activeTab === 'invoices' ? 'active' : ''}`}
            onClick={() => setActiveTab('invoices')}
          >
            <FaFileInvoice /> Invoices
          </button>
          <button 
            className={`nav-btn ${activeTab === 'reports' ? 'active' : ''}`}
            onClick={() => setActiveTab('reports')}
          >
            <FaFileDownload /> Reports
          </button>
          <button 
            className={`nav-btn ${activeTab === 'branches' ? 'active' : ''}`}
            onClick={() => setActiveTab('branches')}
          >
            <FaChartLine /> Branch Sales
          </button>
          <button 
            className={`nav-btn ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => setActiveTab('products')}
          >
            <FaChartPie /> Product Sales
          </button>
          <button 
            className={`nav-btn ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            <FaChartBar /> Analytics
          </button>
        </div>
      </div>

      {activeTab === 'dashboard' && (
        <div className="dashboard-content">
          <div className="dashboard-stats">
            <div className="stat-card income">
              <h3>Monthly Income</h3>
              <p className="stat-value">{formatCurrency(stats.monthlyIncome)}</p>
            </div>
            <div className="stat-card expense">
              <h3>Monthly Expenses</h3>
              <p className="stat-value">{formatCurrency(stats.monthlyExpenses)}</p>
            </div>
            <div className={`stat-card net-flow ${stats.netCashFlow >= 0 ? 'positive' : 'negative'}`}>
              <h3>Net Cash Flow</h3>
              <p className="stat-value">{formatCurrency(stats.netCashFlow)}</p>
            </div>
          </div>

          <div className="dashboard-alerts">
            <div className="alert-card">
              <h3>Quick Snapshot</h3>
              <div className="alert-item">
                <span className="alert-label">Overdue Invoices:</span>
                <span className="alert-value">{stats.overdueInvoices.length}</span>
              </div>
              <div className="alert-item">
                <span className="alert-label">Upcoming Bills:</span>
                <span className="alert-value">{stats.upcomingBills.length}</span>
              </div>
            </div>
          </div>

          <div className="quick-actions">
            <button className="action-btn income" onClick={() => openModal('income')}>
              <FaPlus /> Add Income
            </button>
            <button className="action-btn expense" onClick={() => openModal('expense')}>
              <FaPlus /> Add Expense
            </button>
            <button className="action-btn invoice" onClick={() => openModal('invoice')}>
              <FaPlus /> Create Invoice
            </button>
          </div>

          <div className="dashboard-reset-section">
            <h3>Data Management</h3>
            <div className="reset-actions">
              <button className="reset-btn danger" onClick={handleResetAllTransactions}>
                <FaTrash /> Reset Transactions
              </button>
              <button className="reset-btn danger" onClick={handleResetAllBranchSales}>
                <FaTrash /> Reset Branch Sales
              </button>
              <button className="reset-btn danger" onClick={handleResetAllInvoices}>
                <FaTrash /> Reset Invoices
              </button>
              <button className="reset-btn extreme-danger" onClick={handleResetAllAccountingData}>
                <FaTrash /> Reset Everything
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'transactions' && (
        <div className="transactions-content">
          <div className="transactions-toolbar">
            <div className="toolbar-left">
              <button className="add-btn" onClick={() => openModal('income')}>
                <FaPlus /> Add Income
              </button>
              <button className="add-btn" onClick={() => openModal('expense')}>
                <FaPlus /> Add Expense
              </button>
              <button className="reset-btn danger" onClick={handleResetAllTransactions}>
                <FaTrash /> Reset All Transactions
              </button>
            </div>
            <div className="toolbar-right">
              <div className="search-box">
                <FaSearch />
                <input
                  type="text"
                  placeholder="Search transactions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                <option value="all">All Categories</option>
                {[...expenseCategories, ...incomeCategories].map(cat => (
                  <option key={cat} value={cat}>{cat.replace('_', ' ')}</option>
                ))}
              </select>
              <select value={filterDateRange} onChange={(e) => setFilterDateRange(e.target.value)}>
                <option value="all">All Time</option>
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
                <option value="this_year">This Year</option>
              </select>
            </div>
          </div>

          <div className="transactions-table">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Description</th>
                  <th>Category</th>
                  <th>Amount</th>
                  <th>Receipt</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map(transaction => (
                  <tr key={transaction.id}>
                    <td>{new Date(transaction.date).toLocaleDateString()}</td>
                    <td>
                      <span className={`type-badge ${transaction.type}`}>
                        {transaction.type}
                      </span>
                    </td>
                    <td>{transaction.description}</td>
                    <td>{transaction.category?.replace('_', ' ')}</td>
                    <td className={transaction.type === 'income' ? 'income' : 'expense'}>
                      {transaction.type === 'income' ? '+' : '-'}{formatCurrency(transaction.amount)}
                    </td>
                    <td>
                      {transaction.receiptUrl && (
                        <a href={transaction.receiptUrl} target="_blank" rel="noopener noreferrer">
                          <FaEye />
                        </a>
                      )}
                    </td>
                    <td>
                      <button onClick={() => openModal(transaction.type, transaction)}>
                        <FaEdit />
                      </button>
                      <button onClick={() => handleDeleteTransaction(transaction.id)}>
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'invoices' && (
        <div className="invoices-content">
          <div className="invoices-toolbar">
            <button className="add-btn" onClick={() => openModal('invoice')}>
              <FaPlus /> Create Invoice
            </button>
            <button className="reset-btn danger" onClick={handleResetAllInvoices}>
              <FaTrash /> Reset All Invoices
            </button>
          </div>

          <div className="invoices-table">
            <table>
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Client</th>
                  <th>Amount</th>
                  <th>Due Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(invoice => (
                  <tr key={invoice.id}>
                    <td>{invoice.invoiceNumber}</td>
                    <td>{invoice.clientName}</td>
                    <td>{formatCurrency(invoice.total)}</td>
                    <td>{new Date(invoice.dueDate).toLocaleDateString()}</td>
                    <td>
                      <span className={`status-badge ${invoice.status}`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td>
                      <button onClick={() => openModal('invoice', invoice)}>
                        <FaEdit />
                      </button>
                      <button onClick={() => handleDeleteInvoice(invoice.id)}>
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'reports' && (
        <div className="reports-content">
          <div className="reports-toolbar">
            <button className="export-btn">
              <FaFileDownload /> Export Monthly Summary
            </button>
            <button className="export-btn">
              <FaFileDownload /> Export Tax Report
            </button>
            <button className="reset-btn extreme-danger" onClick={handleResetAllAccountingData}>
              <FaTrash /> Reset All Accounting Data
            </button>
          </div>
          
          <div className="report-cards">
            <div className="report-card">
              <h3>Monthly Summary</h3>
              <p>Income vs Expense for current month</p>
              <button className="generate-btn">Generate Report</button>
            </div>
            
            <div className="report-card">
              <h3>Tax Report</h3>
              <p>Annual tax summary and deductions</p>
              <button className="generate-btn">Generate Report</button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'branches' && (
        <div className="branches-content">
          <div className="branches-toolbar">
            <div className="toolbar-left">
              <h3>Import Monthly Branch Sales Data</h3>
            </div>
            <div className="toolbar-right">
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="month-picker"
              />
              <button className="import-btn" onClick={handleBranchSalesImport}>
                <FaUpload /> Import Data
              </button>
              <button 
                className="test-btn" 
                onClick={() => {
                  try {
                    const parsed = parseBranchSalesData(branchSalesData);
                    console.log('Test parsing result:', parsed);
                    alert(`Test successful! Found ${parsed.length} branches. Check console for details.`);
                  } catch (error) {
                    console.error('Test parsing error:', error);
                    alert(`Test failed: ${error.message}`);
                  }
                }}
              >
                Test Parse Only
              </button>
              <button className="reset-btn danger" onClick={handleResetAllBranchSales}>
                <FaTrash /> Reset All Branch Sales
              </button>
            </div>
          </div>

          <div className="data-import-section">
            <div className="import-instructions">
              <h4>How to Import:</h4>
              <ol>
                <li>Copy your branch sales data from Excel/CSV (including headers)</li>
                <li>Paste it in the text area below</li>
                <li>Select the month</li>
                <li>Click "Import Data"</li>
              </ol>
              <p><strong>Expected format:</strong> Tab-separated values with headers</p>
              
              <div className="format-example">
                <h5>Expected columns (25 total):</h5>
                <div className="column-list">
                  <span>Branch</span>
                  <span>Branch Reference</span>
                  <span>Gross Sales</span>
                  <span>Gross Sales %</span>
                  <span>Net Sales With Tax</span>
                  <span>Taxes</span>
                  <span>Discount Amount</span>
                  <span>Gross Sales Without Tax</span>
                  <span>Net Sales</span>
                  <span>Net Sales %</span>
                  <span>Order Count</span>
                  <span>Average Order</span>
                  <span>Guest Count</span>
                  <span>Average per Guest</span>
                  <span>Customers Count</span>
                  <span>Net Quantity</span>
                  <span>Cost</span>
                  <span>Return Amount</span>
                  <span>Return Quantity</span>
                  <span>Void Amount</span>
                  <span>Void Quantity</span>
                  <span>Profit</span>
                  <span>Charges</span>
                  <span>Rounding Amount</span>
                  <span>Tips</span>
                </div>
              </div>
            </div>

            <div className="data-textarea">
              <textarea
                placeholder="Paste your branch sales data here (tab-separated)..."
                value={branchSalesData}
                onChange={(e) => setBranchSalesData(e.target.value)}
                rows="10"
                className="branch-data-input"
              />
            </div>
          </div>

          <div className="branch-sales-summary">
            <h3>Branch Sales History</h3>
            <div className="sales-grid">
              {branchSales.map(salesRecord => (
                <div key={salesRecord.id} className="sales-card">
                  <div className="sales-card-header">
                    <h4>{new Date(salesRecord.month).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h4>
                    <div className="sales-header-actions">
                      <span className="sales-date">{new Date(salesRecord.createdAt?.seconds * 1000).toLocaleDateString()}</span>
                      <button 
                        className="delete-sales-btn"
                        onClick={() => handleDeleteBranchSales(salesRecord.id)}
                        title="Delete this branch sales record"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                  
                  <div className="sales-metrics">
                    <div className="metric">
                      <span className="metric-label">Total Gross Sales</span>
                      <span className="metric-value">{formatCurrency(salesRecord.totalGrossSales)}</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Total Net Sales</span>
                      <span className="metric-value">{formatCurrency(salesRecord.totalNetSales)}</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Total Profit</span>
                      <span className="metric-value">{formatCurrency(salesRecord.totalProfit)}</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Total Orders</span>
                      <span className="metric-value">{salesRecord.totalOrders}</span>
                    </div>
                  </div>

                  <div className="branch-breakdown">
                    <h5>Branch Breakdown:</h5>
                    <div className="branch-list">
                      {salesRecord.data?.map((branch, index) => (
                        <div key={index} className="branch-item">
                          <span className="branch-name">{branch.branch}</span>
                          <span className="branch-sales">{formatCurrency(branch.netSales)}</span>
                          <span className="branch-orders">{branch.orderCount} orders</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'products' && (
        <div className="products-content">
          <div className="products-toolbar">
            <div className="toolbar-left">
              <h3>Import Monthly Product Sales Data</h3>
            </div>
            <div className="toolbar-right">
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="month-picker"
              />
              <button className="import-btn" onClick={handleImportProductSales}>
                <FaUpload /> Import Data
              </button>
              <button 
                className="test-btn"
                onClick={async () => {
                  try {
                    let dataToTest = productSalesData;
                    
                    if (productSalesFile) {
                      dataToTest = await handleProductSalesFileUpload(productSalesFile);
                    }
                    
                    if (!dataToTest || !dataToTest.trim()) {
                      window.alert('Please either upload a CSV file or paste data first.');
                      return;
                    }
                    
                    const parsed = parseProductSalesData(dataToTest);
                    console.log('Test parsing result:', parsed);
                    window.alert(`Test successful! Found ${parsed.length} products. Check console for details.`);
                  } catch (error) {
                    console.error('Test parsing error:', error);
                    window.alert(`Test failed: ${error.message}`);
                  }
                }}
              >
                Test Parse Only
              </button>
              <button className="reset-btn danger" onClick={handleResetAllProductSales}>
                <FaTrash /> Reset All Product Sales
              </button>
            </div>
          </div>
          <div className="data-import-section">
            <div className="import-instructions">
              <h4>How to Import:</h4>
              <ol>
                <li><strong>Option 1:</strong> Upload a CSV file directly</li>
                <li><strong>Option 2:</strong> Copy your product sales data from Excel/CSV and paste below</li>
                <li>Select the month</li>
                <li>Click "Import Data"</li>
              </ol>
              <p><strong>Supported formats:</strong> CSV files, Tab-separated, or Comma-separated values</p>
              
              <div className="file-upload-section">
                <h5>📁 Upload CSV File (Recommended)</h5>
                <input
                  type="file"
                  accept=".csv,.txt"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    setProductSalesFile(file);
                    if (file) {
                      setProductSalesData(''); // Clear manual input when file is selected
                    }
                  }}
                  className="csv-file-input"
                />
                {productSalesFile && (
                  <div className="file-info">
                    ✅ Selected: {productSalesFile.name} ({(productSalesFile.size / 1024).toFixed(1)} KB)
                    <button 
                      className="clear-file-btn"
                      onClick={() => {
                        setProductSalesFile(null);
                        document.querySelector('.csv-file-input').value = '';
                      }}
                    >
                      ✖️ Clear
                    </button>
                  </div>
                )}
              </div>
              
              <div className="format-example">
                <h5>Expected columns (17 total):</h5>
                <div className="column-list">
                  <span>Product</span>
                  <span>Product SKU</span>
                  <span>Gross Sales</span>
                  <span>Gross Sales %</span>
                  <span>Net Sales With Tax</span>
                  <span>Taxes</span>
                  <span>Discount Amount</span>
                  <span>Gross Sales Without Tax</span>
                  <span>Net Sales</span>
                  <span>Net Sales %</span>
                  <span>Net Quantity</span>
                  <span>Cost</span>
                  <span>Return Amount</span>
                  <span>Return Quantity</span>
                  <span>Void Amount</span>
                  <span>Void Quantity</span>
                  <span>Profit</span>
                </div>
              </div>
            </div>

            <div className="data-textarea">
              <h5>📝 Or Paste Data Manually (Optional)</h5>
              <textarea
                placeholder={productSalesFile ? "File selected above - this field is optional" : "Or paste your product sales data here (CSV format)..."}
                value={productSalesData}
                onChange={(e) => {
                  setProductSalesData(e.target.value);
                  if (e.target.value.trim()) {
                    setProductSalesFile(null); // Clear file when manual input is used
                  }
                }}
                rows="8"
                className="branch-data-input"
                disabled={!!productSalesFile}
                style={{opacity: productSalesFile ? 0.5 : 1}}
              />
            </div>
          </div>

          <div className="product-sales-summary">
            <h3>Product Sales History</h3>
            <div className="sales-grid">
              {productSales.map(salesRecord => (
                <div key={salesRecord.id} className="sales-card">
                  <div className="sales-card-header">
                    <h4>{new Date(salesRecord.month).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h4>
                    <div className="sales-header-actions">
                      <span className="sales-date">{new Date(salesRecord.createdAt?.seconds * 1000).toLocaleDateString()}</span>
                      <button 
                        className="delete-sales-btn"
                        onClick={() => handleDeleteProductSales(salesRecord.id)}
                        title="Delete this product sales record"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                  
                  <div className="sales-metrics">
                    <div className="metric">
                      <span className="metric-label">Total Products</span>
                      <span className="metric-value">{salesRecord.totalProducts}</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Total Gross Sales</span>
                      <span className="metric-value">{formatCurrency(salesRecord.totalGrossSales)}</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Total Net Sales</span>
                      <span className="metric-value">{formatCurrency(salesRecord.totalNetSales)}</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Total Profit</span>
                      <span className="metric-value">{formatCurrency(salesRecord.totalProfit)}</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Total Quantity</span>
                      <span className="metric-value">{salesRecord.totalQuantity}</span>
                    </div>
                  </div>

                  <div className="product-breakdown">
                    <h5>Top Products:</h5>
                    <div className="product-list">
                      {salesRecord.data?.slice(0, 10).map((product, index) => (
                        <div key={index} className="product-item">
                          <span className="product-name">{product.product}</span>
                          <span className="product-sales">{formatCurrency(product.netSales)}</span>
                          <span className="product-quantity">{product.netQuantity} units</span>
                        </div>
                      ))}
                    </div>
                    {salesRecord.data?.length > 10 && (
                      <p className="more-products">... and {salesRecord.data.length - 10} more products</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="analytics-content">
          <div className="analytics-header">
            <h2>Financial Analytics & Insights</h2>
            <div className="analytics-period">
              <FaCalendarAlt />
              <span>Last 12 Months Analysis</span>
            </div>
          </div>

          {/* KPIs Section */}
          <div className="kpis-section">
            <h3>Key Performance Indicators</h3>
            <div className="kpis-grid">
              {(() => {
                const kpis = getFinancialKPIs();
                return (
                  <>
                    <div className="kpi-card">
                      <div className="kpi-icon income">
                        <FaArrowUp />
                      </div>
                      <div className="kpi-content">
                        <h4>Income Growth</h4>
                        <p className={`kpi-value ${kpis.incomeGrowth >= 0 ? 'positive' : 'negative'}`}>
                          {kpis.incomeGrowth >= 0 ? '+' : ''}{kpis.incomeGrowth.toFixed(1)}%
                        </p>
                        <span className="kpi-subtitle">vs last month</span>
                      </div>
                    </div>
                    
                    <div className="kpi-card">
                      <div className="kpi-icon expense">
                        <FaArrowDown />
                      </div>
                      <div className="kpi-content">
                        <h4>Expense Growth</h4>
                        <p className={`kpi-value ${kpis.expenseGrowth <= 0 ? 'positive' : 'negative'}`}>
                          {kpis.expenseGrowth >= 0 ? '+' : ''}{kpis.expenseGrowth.toFixed(1)}%
                        </p>
                        <span className="kpi-subtitle">vs last month</span>
                      </div>
                    </div>
                    
                    <div className="kpi-card">
                      <div className="kpi-icon profit">
                        <FaChartLine />
                      </div>
                      <div className="kpi-content">
                        <h4>Profit Margin</h4>
                        <p className={`kpi-value ${kpis.profitMargin >= 0 ? 'positive' : 'negative'}`}>
                          {kpis.profitMargin.toFixed(1)}%
                        </p>
                        <span className="kpi-subtitle">this month</span>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>

          {/* Monthly Trends Chart */}
          <div className="chart-section">
            <h3>Monthly Trends (Last 12 Months)</h3>
            <div className="chart-container">
              <div className="chart-header">
                <div className="chart-legend">
                  <div className="legend-item">
                    <div className="legend-color income"></div>
                    <span>Income</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color expense"></div>
                    <span>Expenses</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color profit"></div>
                    <span>Profit</span>
                  </div>
                </div>
              </div>
              <div className="simple-chart">
                {getMonthlyTrends().map((month, index) => (
                  <div key={month.month} className="chart-bar-group">
                    <div className="chart-bars">
                      <div 
                        className="chart-bar income"
                        style={{ 
                          height: `${Math.max(5, (month.income / Math.max(...getMonthlyTrends().map(m => Math.max(m.income, m.expenses)))) * 100)}%`
                        }}
                        title={`Income: ${formatCurrency(month.income)}`}
                      ></div>
                      <div 
                        className="chart-bar expense"
                        style={{ 
                          height: `${Math.max(5, (month.expenses / Math.max(...getMonthlyTrends().map(m => Math.max(m.income, m.expenses)))) * 100)}%`
                        }}
                        title={`Expenses: ${formatCurrency(month.expenses)}`}
                      ></div>
                      <div 
                        className="chart-bar profit"
                        style={{ 
                          height: `${Math.max(5, Math.abs(month.profit) / Math.max(...getMonthlyTrends().map(m => Math.max(m.income, m.expenses))) * 100)}%`,
                          backgroundColor: month.profit >= 0 ? '#10b981' : '#ef4444'
                        }}
                        title={`Profit: ${formatCurrency(month.profit)}`}
                      ></div>
                    </div>
                    <div className="chart-label">{month.monthName}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Category Analysis */}
          <div className="analysis-grid">
            <div className="analysis-card">
              <h3>Income by Category</h3>
              <div className="category-breakdown">
                {(() => {
                  const categories = getCategoryAnalysis().income;
                  const total = categories.reduce((sum, cat) => sum + cat.amount, 0);
                  
                  return categories.map(category => (
                    <div key={category.category} className="category-item">
                      <div className="category-header">
                        <span className="category-name">{category.category}</span>
                        <span className="category-amount">{formatCurrency(category.amount)}</span>
                      </div>
                      <div className="category-bar">
                        <div 
                          className="category-fill income"
                          style={{ width: `${total > 0 ? (category.amount / total) * 100 : 0}%` }}
                        ></div>
                      </div>
                      <div className="category-percentage">
                        {total > 0 ? ((category.amount / total) * 100).toFixed(1) : 0}%
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>

            <div className="analysis-card">
              <h3>Expenses by Category</h3>
              <div className="category-breakdown">
                {(() => {
                  const categories = getCategoryAnalysis().expenses;
                  const total = categories.reduce((sum, cat) => sum + cat.amount, 0);
                  
                  return categories.map(category => (
                    <div key={category.category} className="category-item">
                      <div className="category-header">
                        <span className="category-name">{category.category}</span>
                        <span className="category-amount">{formatCurrency(category.amount)}</span>
                      </div>
                      <div className="category-bar">
                        <div 
                          className="category-fill expense"
                          style={{ width: `${total > 0 ? (category.amount / total) * 100 : 0}%` }}
                        ></div>
                      </div>
                      <div className="category-percentage">
                        {total > 0 ? ((category.amount / total) * 100).toFixed(1) : 0}%
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>

          {/* Branch Performance */}
          {getBranchAnalysis().length > 0 && (
            <div className="branch-analysis">
              <h3>Branch Performance Analysis</h3>
              <div className="branch-performance-grid">
                {getBranchAnalysis().map(branch => (
                  <div key={branch.branch} className="branch-performance-card">
                    <div className="branch-header">
                      <h4>{branch.branch}</h4>
                      <span className="branch-months">{branch.months} months</span>
                    </div>
                    <div className="branch-metrics">
                      <div className="branch-metric">
                        <span className="metric-label">Total Sales</span>
                        <span className="metric-value">{formatCurrency(branch.totalSales)}</span>
                      </div>
                      <div className="branch-metric">
                        <span className="metric-label">Avg Monthly Sales</span>
                        <span className="metric-value">{formatCurrency(branch.avgMonthlySales)}</span>
                      </div>
                      <div className="branch-metric">
                        <span className="metric-label">Total Orders</span>
                        <span className="metric-value">{branch.totalOrders.toLocaleString()}</span>
                      </div>
                      <div className="branch-metric">
                        <span className="metric-label">Avg Order Value</span>
                        <span className="metric-value">{formatCurrency(branch.avgOrderValue)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top Performers */}
          <div className="top-performers">
            <h3>This Month's Top Performers</h3>
            <div className="performers-grid">
              <div className="performers-card">
                <h4>Top Income Sources</h4>
                <div className="performers-list">
                  {getTopPerformers().topIncomes.map((transaction, index) => (
                    <div key={transaction.id} className="performer-item">
                      <div className="performer-rank">#{index + 1}</div>
                      <div className="performer-details">
                        <span className="performer-name">{transaction.description}</span>
                        <span className="performer-source">{transaction.source}</span>
                      </div>
                      <div className="performer-amount income">{formatCurrency(transaction.amount)}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="performers-card">
                <h4>Top Expenses</h4>
                <div className="performers-list">
                  {getTopPerformers().topExpenses.map((transaction, index) => (
                    <div key={transaction.id} className="performer-item">
                      <div className="performer-rank">#{index + 1}</div>
                      <div className="performer-details">
                        <span className="performer-name">{transaction.description}</span>
                        <span className="performer-source">{transaction.vendor}</span>
                      </div>
                      <div className="performer-amount expense">{formatCurrency(transaction.amount)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>
                {editingTransaction ? 'Edit' : 'Add'} {' '}
                {modalType === 'income' ? 'Income' : modalType === 'expense' ? 'Expense' : 'Invoice'}
              </h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
            </div>
            
            <div className="modal-content">
              {modalType === 'invoice' ? (
                <form onSubmit={handleSubmitInvoice}>
                  <div className="form-group">
                    <label>Invoice Number</label>
                    <input
                      type="text"
                      value={invoiceFormData.invoiceNumber}
                      onChange={(e) => setInvoiceFormData({...invoiceFormData, invoiceNumber: e.target.value})}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Client Name</label>
                    <input
                      type="text"
                      value={invoiceFormData.clientName}
                      onChange={(e) => setInvoiceFormData({...invoiceFormData, clientName: e.target.value})}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Client Email</label>
                    <input
                      type="email"
                      value={invoiceFormData.clientEmail}
                      onChange={(e) => setInvoiceFormData({...invoiceFormData, clientEmail: e.target.value})}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Due Date</label>
                    <input
                      type="date"
                      value={invoiceFormData.dueDate}
                      onChange={(e) => setInvoiceFormData({...invoiceFormData, dueDate: e.target.value})}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Total Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      value={invoiceFormData.total}
                      onChange={(e) => setInvoiceFormData({...invoiceFormData, total: parseFloat(e.target.value)})}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      value={invoiceFormData.status}
                      onChange={(e) => setInvoiceFormData({...invoiceFormData, status: e.target.value})}
                    >
                      <option value="unpaid">Unpaid</option>
                      <option value="paid">Paid</option>
                      <option value="overdue">Overdue</option>
                    </select>
                  </div>
                  
                  <div className="form-actions">
                    <button type="button" onClick={() => setShowModal(false)}>Cancel</button>
                    <button type="submit" className="submit-btn">
                      {editingTransaction ? 'Update' : 'Create'} Invoice
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleSubmitTransaction}>
                  <div className="form-group">
                    <label>Date</label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({...formData, date: e.target.value})}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({...formData, amount: e.target.value})}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      required
                    >
                      <option value="">Select Category</option>
                      {(modalType === 'income' ? incomeCategories : expenseCategories).map(cat => (
                        <option key={cat} value={cat}>{cat.replace('_', ' ')}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Description</label>
                    <input
                      type="text"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      required
                    />
                  </div>
                  
                  {modalType === 'income' ? (
                    <div className="form-group">
                      <label>Source</label>
                      <input
                        type="text"
                        value={formData.source}
                        onChange={(e) => setFormData({...formData, source: e.target.value})}
                      />
                    </div>
                  ) : (
                    <div className="form-group">
                      <label>Vendor</label>
                      <input
                        type="text"
                        value={formData.vendor}
                        onChange={(e) => setFormData({...formData, vendor: e.target.value})}
                      />
                    </div>
                  )}
                  
                  <div className="form-group">
                    <label>Receipt</label>
                    <div className="file-upload">
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => setReceiptFile(e.target.files[0])}
                      />
                      {receiptFile && (
                        <p className="file-name">{receiptFile.name}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label>Notes</label>
                    <textarea
                      value={formData.note}
                      onChange={(e) => setFormData({...formData, note: e.target.value})}
                      rows="3"
                    />
                  </div>
                  
                  <div className="form-actions">
                    <button type="button" onClick={() => setShowModal(false)}>Cancel</button>
                    <button type="submit" className="submit-btn" disabled={uploading}>
                      {uploading ? 'Uploading...' : (editingTransaction ? 'Update' : 'Add')} {modalType}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAccountingPage;